<div class="geocoding-container settings-page">

  <div class="settings-page__content">
    <p class="settings-page__description">
      {{ 'SETTINGS_GEOCODING.DESCRIPTION' | translate }}
    </p>

    @if (initialLoadFailed() && loadError(); as error) {
      <tf-settings-load-error
        [title]="error"
        [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
        [details]="loadErrorDetails()"
        [retrying]="isLoading()"
        (retry)="retryLoad()" />
    } @else if (!hasLoadedOnce()) {
      <div class="geocoding-loading-skeleton" aria-hidden="true">
        <section class="geocoding-loading-skeleton__card">
          <span class="geocoding-loading-skeleton__bar geocoding-loading-skeleton__bar--title"></span>
          <span class="geocoding-loading-skeleton__bar geocoding-loading-skeleton__bar--text"></span>
          <span class="geocoding-loading-skeleton__control"></span>
        </section>
        <section class="geocoding-loading-skeleton__card">
          <span class="geocoding-loading-skeleton__bar geocoding-loading-skeleton__bar--title"></span>
          <span class="geocoding-loading-skeleton__bar geocoding-loading-skeleton__bar--text"></span>
          <span class="geocoding-loading-skeleton__control geocoding-loading-skeleton__control--wide"></span>
          <div class="geocoding-loading-skeleton__grid">
            <span class="geocoding-loading-skeleton__control"></span>
            <span class="geocoding-loading-skeleton__control"></span>
          </div>
          <span class="geocoding-loading-skeleton__map"></span>
        </section>
      </div>
    } @else {
      <div class="geocoding-cards">
        <!-- Card 1: Default country -->
        <section class="geocoding-card">
          <tf-settings-inline-message
            [message]="saveError()"
            messageId="geocoding-settings-save-error"
            (dismissed)="clearSaveError()" />

          <div class="geocoding-card__header">
            <h3 class="settings-page__section-title">
              {{ 'SETTINGS_GEOCODING.DEFAULT_COUNTRY' | translate }}
            </h3>
            <p class="settings-page__description">
              {{ 'SETTINGS_GEOCODING.DEFAULT_COUNTRY_DESCRIPTION' | translate }}
            </p>
          </div>

          <div class="form-group settings-page__field">
            <label for="defaultCountry" class="settings-page__label">
              {{ 'SETTINGS_GEOCODING.DEFAULT_COUNTRY' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <p-select
                id="defaultCountry"
                [options]="countryOptions()"
                [ngModel]="selectedCountry()"
                (ngModelChange)="onCountryChange($event)"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                [showClear]="true"
                [placeholder]="'SETTINGS_GEOCODING.DEFAULT_COUNTRY_PLACEHOLDER' | translate"
                [disabled]="!canUpdateGeocoding()"
                [class.ng-invalid]="!!serverErrors.value()['defaultCountry']"
                [attr.aria-invalid]="serverErrors.value()['defaultCountry'] ? true : null"
                [attr.aria-describedby]="
                  serverErrors.value()['defaultCountry'] ? 'defaultCountry-error' : null
                "
                styleClass="w-full" />
            </div>
            @if (submitted() && settingsForm.defaultCountry().invalid()) {
              <small class="p-error">
                {{ settingsForm.defaultCountry().errors()[0]?.message ?? '' | translate }}
              </small>
            }
            @if (serverErrors.value()['defaultCountry']; as error) {
              <small class="p-error" id="defaultCountry-error" role="alert">{{ error }}</small>
            }
          </div>
        </section>

        <!-- Card 2: Default search focus point -->
        <section class="geocoding-card">
          <div class="geocoding-card__header">
            <h3 class="settings-page__section-title">
              {{ 'SETTINGS_GEOCODING.DEFAULT_FOCUS_POINT' | translate }}
            </h3>
            <p class="settings-page__description">
              {{ 'SETTINGS_GEOCODING.DEFAULT_FOCUS_POINT_DESCRIPTION' | translate }}
            </p>
          </div>

          <div class="form-group settings-page__field">
            <label for="placeSearch" class="settings-page__label">
              {{ 'SETTINGS_GEOCODING.SEARCH_FOCUS_POINT' | translate }}
            </label>
            <div class="settings-page__field-input-wrap geocoding-search-wrap">
              <p-autoComplete
                inputId="placeSearch"
                [ngModel]="placeSearchQuery()"
                (ngModelChange)="placeSearchQuery.set($event)"
                [suggestions]="placeSuggestions()"
                field="label"
                [placeholder]="'SETTINGS_GEOCODING.SEARCH_FOCUS_POINT_PLACEHOLDER' | translate"
                [emptyMessage]="'SETTINGS_GEOCODING.SEARCH_NO_RESULTS' | translate"
                [delay]="300"
                [minLength]="3"
                [forceSelection]="false"
                [disabled]="!canUpdateGeocoding()"
                [fluid]="true"
                appendTo="body"
                (completeMethod)="onPlaceSearch($event)"
                (onSelect)="onPlaceSelected($event)" />
            </div>
            <small class="settings-page__hint">
              {{ 'SETTINGS_GEOCODING.SEARCH_FOCUS_POINT_HINT' | translate }}
            </small>
          </div>

          <div class="geocoding-coords-row">
            <div class="form-group settings-page__field">
              <label for="geocoding-lat" class="settings-page__label">
                {{ 'SETTINGS_GEOCODING.LATITUDE' | translate }}
              </label>
              <div class="settings-page__field-input-wrap">
                <input
                  pInputText
                  id="geocoding-lat"
                  type="number"
                  step="any"
                  [ngModel]="formModel().defaultFocusPointLat ?? ''"
                  (ngModelChange)="onLatitudeChange($event === '' ? null : +$event)"
                  [placeholder]="'SETTINGS_GEOCODING.LATITUDE_PLACEHOLDER' | translate"
                  [disabled]="!canUpdateGeocoding()"
                  [class.ng-invalid]="!!serverErrors.value()['defaultFocusPointLat']"
                  [attr.aria-invalid]="serverErrors.value()['defaultFocusPointLat'] ? true : null"
                  [attr.aria-describedby]="
                    serverErrors.value()['defaultFocusPointLat'] ? 'geocoding-lat-error' : null
                  "
                  class="w-full" />
              </div>
              @if (submitted() && settingsForm.defaultFocusPointLat().invalid()) {
                <small class="p-error">
                  {{ settingsForm.defaultFocusPointLat().errors()[0]?.message ?? '' | translate }}
                </small>
              }
              @if (serverErrors.value()['defaultFocusPointLat']; as error) {
                <small class="p-error" id="geocoding-lat-error" role="alert">{{ error }}</small>
              }
            </div>

            <div class="form-group settings-page__field">
              <label for="geocoding-lon" class="settings-page__label">
                {{ 'SETTINGS_GEOCODING.LONGITUDE' | translate }}
              </label>
              <div class="settings-page__field-input-wrap">
                <input
                  pInputText
                  id="geocoding-lon"
                  type="number"
                  step="any"
                  [ngModel]="formModel().defaultFocusPointLon ?? ''"
                  (ngModelChange)="onLongitudeChange($event === '' ? null : +$event)"
                  [placeholder]="'SETTINGS_GEOCODING.LONGITUDE_PLACEHOLDER' | translate"
                  [disabled]="!canUpdateGeocoding()"
                  [class.ng-invalid]="!!serverErrors.value()['defaultFocusPointLon']"
                  [attr.aria-invalid]="serverErrors.value()['defaultFocusPointLon'] ? true : null"
                  [attr.aria-describedby]="
                    serverErrors.value()['defaultFocusPointLon'] ? 'geocoding-lon-error' : null
                  "
                  class="w-full" />
              </div>
              @if (submitted() && settingsForm.defaultFocusPointLon().invalid()) {
                <small class="p-error">
                  {{ settingsForm.defaultFocusPointLon().errors()[0]?.message ?? '' | translate }}
                </small>
              }
              @if (serverErrors.value()['defaultFocusPointLon']; as error) {
                <small class="p-error" id="geocoding-lon-error" role="alert">{{ error }}</small>
              }
            </div>
          </div>

          <div class="geocoding-map">
            <tf-settings-geocoding-map
              [focusPoint]="currentFocusPoint()"
              (focusPointChange)="onFocusPointChange($event)" />
            <small class="settings-page__hint">
              {{ 'SETTINGS_GEOCODING.MAP_INTERACTION_HINT' | translate }}
            </small>
          </div>
        </section>
      </div>
    }
  </div>
  @if (hasLoadedOnce() && !initialLoadFailed()) {
    <tf-settings-actions
      (cancelled)="cancel()"
      (save)="saveSettings()"
      [disabled]="
        settingsForm().invalid() || isLoading() || isSaving() || !canUpdateGeocoding() || !!loadError()
      "
      [loading]="isSaving()" />
  }
</div>
